/**
 * KeyExposureDialog — who might have seen a key's CURRENT value (plan
 * docs/plans/2026-10-16_KEYBOOK_EXPOSURE.md, model C): the roles and teams that
 * could open it, the people it was shared with directly, and everyone who opened
 * it — including any who have since lost access. A role's or team's members are
 * not listed. Opened from the key detail's exposure line; managers only (the
 * server answers GET /entries/:id/exposure for Manage).
 *
 * Grouped like the access dialog (People & teams · PDApp & AI · Outsiders),
 * the same glass dialog, portaled the same way (theme class on the content,
 * never the backdrop).
 *
 * e2e: keybook-exposure-dialog · exposure-view-people|apps|outsiders ·
 * exposure-row (+data-principal-type, data-still-has-access).
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass, avatarBg, getInitials } from '@papan/core-ui/ui'
import { erasedOwnerEmail } from '@papan/types'
import { X, Loader2, KeyRound, Box, Network, Shield, UsersRound } from 'lucide-react'
import { keybookApi, type KeybookExposure } from '../client/api.js'
import {
  exposureEntities, exposureEntityName, exposureViaLabel, shortDate, nameFromEmail, type ExposureKind,
} from './exposure-view.js'

type ViewMode = 'people' | 'apps' | 'outsiders'

interface KeyExposureDialogProps {
  entryId: string
  label: string
  onClose: () => void
}

export function KeyExposureDialog({ entryId, label, onClose }: KeyExposureDialogProps) {
  const { t, i18n } = useTranslation('keybook')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [view, setView] = useState<ViewMode>('people')
  const [exposure, setExposure] = useState<KeybookExposure | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    keybookApi.getEntryExposure(entryId)
      .then(x => { if (alive) setExposure(x) })
      .catch((err: any) => { if (alive) setError(err?.message || t('exposure.loadFailed')) })
    return () => { alive = false }
  }, [entryId, t])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const VIEWS: { id: ViewMode; label: string; Icon: typeof UsersRound; kinds: ExposureKind[]; empty: string }[] = [
    { id: 'people', label: t('access.viewPeople'), Icon: UsersRound, kinds: ['role', 'team', 'email'], empty: t('exposure.emptyPeople') },
    { id: 'apps', label: t('access.viewApps'), Icon: Box, kinds: ['app'], empty: t('exposure.emptyApps') },
    { id: 'outsiders', label: t('access.viewOutsiders'), Icon: Network, kinds: ['tenant'], empty: t('exposure.emptyOutsiders') },
  ]
  const current = VIEWS.find(v => v.id === view)!

  const renderRows = () => {
    if (!exposure) return null
    // Roles first (the organization owner first), then teams, then people.
    const rows = exposureEntities(exposure).filter(e => current.kinds.includes(e.kind))
    if (rows.length === 0) return <p className="text-sm themed-text-muted">{current.empty}</p>
    return (
      <ul className="kbg-well p-1.5 space-y-0.5">
        {rows.map(entity => {
          const { kind, item } = entity
          const erased = kind === 'email' ? erasedOwnerEmail(item.id) : null
          const name = kind === 'email' && !erased && !item.name ? nameFromEmail(item.id) : exposureEntityName(t, entity)
          const kindLabel = kind === 'role' ? t('access.roleSub')
            : kind === 'team' ? t('access.teamSub')
              : kind === 'email' ? (erased ? null : item.id)
                : kind === 'app' ? t('exposure.kindApp') : t('exposure.kindTenant')
          // A person: how they can open it NOW (or "No access now") — only in the
          // full answer (D29: a viewer without Manage gets null) — and when they
          // opened it. A role / team: since when it could.
          const sub = (kind === 'email'
            ? [kindLabel, item.stillHasAccess === null ? null : exposureViaLabel(t, item.via), item.openedAt ? t('exposure.openedAt', { date: shortDate(item.openedAt, i18n.language) }) : null]
            : [kindLabel, t('exposure.firstAt', { date: shortDate(item.firstAt, i18n.language) })]
          ).filter(Boolean).join(' · ')
          return (
            <li
              key={`${kind}:${item.id}`}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg"
              data-testid="exposure-row"
              data-principal-type={kind}
              data-still-has-access={item.stillHasAccess === null ? undefined : item.stillHasAccess || undefined}
            >
              {kind === 'email' ? (
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0 select-none ${avatarBg(erased ?? item.id)}`} aria-hidden>
                  {getInitials(erased ?? item.id)}
                </span>
              ) : (
                <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${kind === 'role' ? 'bg-amber-500' : kind === 'team' ? 'bg-blue-500' : 'bg-violet-500'}`} aria-hidden>
                  {kind === 'role' ? <Shield size={13} className="text-white" />
                    : kind === 'team' ? <UsersRound size={13} className="text-white" />
                      : kind === 'app' ? <Box size={13} className="text-white" />
                        : <Network size={13} className="text-white" />}
                </span>
              )}
              <span className={`flex-1 min-w-0 ${item.stillHasAccess === false ? 'opacity-70' : ''}`}>
                <span className="block text-sm themed-text truncate">{name}</span>
                <span className="block text-[11px] themed-text-muted break-words">{sub}</span>
              </span>
              {item.stillHasAccess === false && kind !== 'email' && (
                <span className="kbg-pill shrink-0 text-[10px] px-1.5 py-0.5 text-amber-400">{t('exposure.noLongerHasAccess')}</span>
              )}
            </li>
          )
        })}
      </ul>
    )
  }

  // Portaled: in-tree fixed overlays underlap the taskbar (ancestor stacking
  // context) — as KeyAccessDialog.
  return createPortal(
    <div className="kb-deck-portal fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className={`${themeClass} ws-glass-dialog w-full max-w-lg p-6 relative z-10 max-h-[85vh] flex flex-col`}
        role="dialog"
        aria-label={t('exposure.title')}
        data-testid="keybook-exposure-dialog"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 themed-text-muted hover:themed-text cursor-pointer"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-lg font-semibold themed-text mb-1">{t('exposure.title')}</h2>
        <div className="flex items-center gap-1.5 mb-1 text-sm themed-text-muted min-w-0">
          <KeyRound className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} />
          <span className="truncate">{label}</span>
          {exposure && (
            <span className="shrink-0">· {t('exposure.since', { date: shortDate(exposure.since, i18n.language) })}</span>
          )}
        </div>
        <p className="text-[11px] themed-text-muted mb-3 leading-snug">{t('exposure.note')}</p>

        <div className="kbg-well flex gap-1 mb-4 p-1" role="tablist" aria-label={t('exposure.title')}>
          {VIEWS.map(({ id, label: viewLabel, Icon }) => {
            const active = view === id
            return (
              <button
                key={id}
                role="tab"
                aria-selected={active}
                data-testid={`exposure-view-${id}`}
                onClick={() => setView(id)}
                className={`flex-1 px-2 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                  active ? 'bg-[var(--ws-pill-active-bg)] themed-text font-medium' : 'themed-text-muted hover:themed-text'
                }`}
              >
                <Icon size={13} />
                {viewLabel}
              </button>
            )
          })}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
          {error ? (
            <p className="text-xs text-red-400">{error}</p>
          ) : !exposure ? (
            <div className="flex items-center justify-center py-6 themed-text-muted">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : renderRows()}
        </div>
      </div>
    </div>,
    document.body,
  )
}
